import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ====== Timezone helpers: America/Cuiaba (UTC-4) ======
export const CUIABA_TZ = 'America/Cuiaba';

// Returns today's date in ISO format (YYYY-MM-DD) for Cuiabá timezone
export function todayIsoDateCuiaba(): string {
  // en-CA yields YYYY-MM-DD reliably
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CUIABA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// Format ISO date (YYYY-MM-DD) to pt-BR date (DD/MM/YYYY)
export function formatIsoDateToPtBr(dateIso?: string | null): string {
  if (!dateIso) return '';
  const [y, m, d] = dateIso.split('-');
  if (!y || !m || !d) return dateIso;
  return `${d}/${m}/${y}`;
}

// Compose a human label for date + time in Cuiabá, without relying on local timezone parsing
export function formatDateTimeLabel(dateIso: string, timeHHmm?: string): string {
  const dateStr = formatIsoDateToPtBr(dateIso);
  const timeStr = timeHHmm ? `${timeHHmm}` : '';
  return timeStr ? `${dateStr} ${timeStr} (UTC-4 Cuiabá)` : `${dateStr} (UTC-4 Cuiabá)`;
}

// Returns current date and time for Cuiabá timezone as ISO date and HH:mm
export function nowCuiabaDateTime(): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CUIABA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value || '';
  const date = `${get('year')}-${get('month')}-${get('day')}`;
  const time = `${get('hour')}:${get('minute')}`;
  return { date, time };
}

// Converts a Cuiabá local date/time (YYYY-MM-DD, HH:mm) to UTC epoch milliseconds.
// Cuiabá runs on UTC-4 (no DST), so we add 4 hours to reach UTC.
export function cuiabaDateTimeToUtcMillis(dateIso: string, timeHHmm: string): number {
  const [yearStr, monthStr, dayStr] = dateIso.split('-');
  const [hourStr, minuteStr] = timeHHmm.split(':');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1; // JS months are 0-based
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  // Convert Cuiabá time (UTC-4) to UTC by adding 4 hours
  return Date.UTC(year, month, day, hour + 4, minute, 0, 0);
}

// Returns current time in UTC epoch milliseconds, based on Cuiabá timezone clock.
export function nowCuiabaUtcMillis(): number {
  const now = nowCuiabaDateTime();
  return cuiabaDateTimeToUtcMillis(now.date, now.time);
}

// Compares now (in Cuiabá) with a scheduled date/time (YYYY-MM-DD, HH:mm)
export function isNowPastScheduleCuiaba(dateIso?: string, timeHHmm?: string): boolean {
  if (!dateIso || !timeHHmm) return false;
  const now = nowCuiabaDateTime();
  if (now.date > dateIso) return true;
  if (now.date < dateIso) return false;
  return now.time >= timeHHmm;
}

/**
 * Converte um valor monetário em reais para centavos inteiros, preservando precisão.
 * Regra de arredondamento: half-up (0.5 arredonda para cima).
 */
export function toCents(value: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error('Valor monetário deve ser numérico');
  }
  return Math.round(value * 100);
}

/**
 * Converte centavos inteiros para reais com duas casas decimais.
 */
export function fromCents(cents: number): number {
  if (!Number.isInteger(cents)) {
    throw new Error('Centavos devem ser inteiros');
  }
  return cents / 100;
}

/**
 * Soma valores monetários (em reais), operando em centavos para evitar erros de ponto flutuante.
 */
export function addMoney(values: number[]): number {
  const totalCents = values.reduce((sum, v) => sum + toCents(v || 0), 0);
  return fromCents(totalCents);
}

/**
 * Multiplica um valor monetário por uma taxa, com arredondamento para centavos (half-up).
 * Ex.: 50% (0.5) de 1.356.854,86 → 678.427,43.
 */
export function multiplyMoneyByRate(value: number, rate: number): number {
  if (typeof rate !== 'number' || Number.isNaN(rate)) {
    throw new Error('Taxa deve ser numérica');
  }
  const cents = toCents(value);
  const resultCents = Math.round(cents * rate);
  return fromCents(resultCents);
}

/**
 * Divide um valor monetário por um divisor, com arredondamento para centavos (half-up).
 */
export function divideMoney(value: number, divisor: number): number {
  if (typeof divisor !== 'number' || Number.isNaN(divisor) || divisor === 0) {
    throw new Error('Divisor inválido');
  }
  const cents = toCents(value);
  const resultCents = Math.round(cents / divisor);
  return fromCents(resultCents);
}

/**
 * Formata valor monetário em BRL com duas casas decimais.
 */
export function formatMoneyBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// =============================
// Sequência de Emendas (Global)
// =============================

export type AmendmentSequenceResult = {
  councilorCode: string;
  start: number;
  end: number;
  codes: string[];
};

function padSequential(n: number): string {
  return String(n).padStart(3, '0');
}

function assertCouncilorCode(code: string) {
  if (!/^\d{3}$/.test(code) || code === '000') {
    throw new Error('Código do vereador inválido: use 3 dígitos entre 001 e 999');
  }
}

function assertPositiveInteger(name: string, value: number) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error(`${name} deve ser um inteiro positivo`);
  }
}

function assertNonNegativeInteger(name: string, value: number) {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`${name} deve ser um inteiro não negativo`);
  }
}

// Gera sequência global de emendas para um vereador específico,
// assegurando continuidade (sem lacunas) e sem sobreposição com o último sequencial usado.
export function generateAmendmentSequence(
  councilorCode: string,
  amendmentCount: number,
  lastSequentialUsed: number
): AmendmentSequenceResult {
  assertCouncilorCode(councilorCode);
  assertPositiveInteger('Quantidade de emendas', amendmentCount);
  assertNonNegativeInteger('Último sequencial utilizado', lastSequentialUsed);

  const start = lastSequentialUsed + 1;
  const end = lastSequentialUsed + amendmentCount;

  // Sem lacunas: sequência é contínua de start..end
  // Sem sobreposição: começa estritamente após lastSequentialUsed
  const codes = Array.from({ length: amendmentCount }, (_, i) => {
    const seq = start + i;
    return `${councilorCode}-${padSequential(seq)}`;
  });

  return { councilorCode, start, end, codes };
}

// Valida uma lista de blocos (na ordem fornecida) assegurando sequência global contínua.
// Útil para checar vários vereadores em sequência.
export function validateGlobalSequenceBlocks(
  blocks: Array<{ councilorCode: string; amendmentCount: number }>,
  lastSequentialUsed: number
) {
  assertNonNegativeInteger('Último sequencial utilizado', lastSequentialUsed);

  let current = lastSequentialUsed;
  for (const b of blocks) {
    assertCouncilorCode(b.councilorCode);
    assertPositiveInteger('Quantidade de emendas', b.amendmentCount);
    // Próximo bloco deve iniciar em current+1
    const expectedStart = current + 1;
    const expectedEnd = current + b.amendmentCount;
    // Avança ponteiro global
    current = expectedEnd;
    // Nenhum retorno necessário; se algum bloco for inválido, exceções são lançadas acima
  }
  return { finalSequential: current };
}

// Recalcula a numeração sequencial global (1..N) com base em data (ISO) e id como desempate.
// Retorna um mapa id -> sequencial (com padding de 3 dígitos).
export function recomputeGlobalSequentialNumbers<T extends { id: string; date?: string }>(items: T[]): Map<string, string> {
  const sorted = [...items].sort((a, b) => {
    const da = a.date || '';
    const db = b.date || '';
    if (da === db) return a.id.localeCompare(b.id);
    return da.localeCompare(db);
  });
  const map = new Map<string, string>();
  sorted.forEach((it, idx) => map.set(it.id, String(idx + 1).padStart(3, '0')));
  return map;
}

// Recalcula a numeração sequencial global (1..N) ordenando primeiro pelo código do vereador (3 dígitos),
// depois por data ISO (YYYY-MM-DD) e por id como desempate. Útil para garantir que a tabela, ao ser
// ordenada por vereador e emenda, siga sequência perfeita sem regressões entre vereadores.
export function recomputeGlobalSequentialNumbersByCouncilor<T extends { id: string; date?: string; councilorId: string }>(
  items: T[],
  councilorCodeById: Map<string, string>
): Map<string, string> {
  const sorted = [...items].sort((a, b) => {
    const ac = councilorCodeById.get(a.councilorId) || '000';
    const bc = councilorCodeById.get(b.councilorId) || '000';
    if (ac !== bc) return ac.localeCompare(bc);
    const da = a.date || '';
    const db = b.date || '';
    if (da !== db) return da.localeCompare(db);
    return a.id.localeCompare(b.id);
  });
  const map = new Map<string, string>();
  sorted.forEach((it, idx) => map.set(it.id, String(idx + 1).padStart(3, '0')));
  return map;
}

// Recalcula numeração sequencial por vereador: para cada vereador, reinicia a contagem em 001,
// ordenando as emendas por data ISO e id como desempate. Retorna id -> sequencial (3 dígitos).
export function recomputeSequentialNumbersPerCouncilor<T extends { id: string; date?: string; councilorId: string }>(
  items: T[],
  councilorCodeById: Map<string, string>
): Map<string, string> {
  const groups = new Map<string, T[]>();
  for (const it of items) {
    const arr = groups.get(it.councilorId) || [];
    arr.push(it);
    groups.set(it.councilorId, arr);
  }
  const map = new Map<string, string>();
  // Percorre vereadores na ordem de código para previsibilidade visual
  const councilorIdsSorted = Array.from(groups.keys()).sort((a, b) => {
    const ac = councilorCodeById.get(a) || '999';
    const bc = councilorCodeById.get(b) || '999';
    return ac.localeCompare(bc);
  });
  for (const councilorId of councilorIdsSorted) {
    const list = (groups.get(councilorId) || []).sort((a, b) => {
      const da = a.date || '';
      const db = b.date || '';
      if (da !== db) return da.localeCompare(db);
      return a.id.localeCompare(b.id);
    });
    list.forEach((it, idx) => {
      map.set(it.id, String(idx + 1).padStart(3, '0'));
    });
  }
  return map;
}

// Gera mapa de códigos de vereadores (3 dígitos) pela ordem alfabética de nome.
export function councilorCodeMap<T extends { id: string; name: string }>(councilors: T[]): Map<string, string> {
  const sorted = [...councilors].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  const map = new Map<string, string>();
  sorted.forEach((c, idx) => map.set(c.id, String(idx + 1).padStart(3, '0')));
  return map;
}

// Constrói identificação `${código_vereador}-${número_sequencial}-${ano}`
export function buildIdentification(councilorCode: string, sequential: string, dateIso?: string): string {
  const year = (dateIso || '').slice(0, 4) || '';
  return `${councilorCode}-${sequential}-${year}`;
}
